var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useEffect, useMemo, useState, } from "react";
import { CartServiceFactory } from "@/api/cart/CartServiceFactory";
const CartContext = createContext(undefined);
export const CartProvider = ({ children, }) => {
    const [cartService, setCartService] = useState(null);
    const [cart, setCart] = useState(null);
    const [loadingItems, setLoadingItems] = useState([]);
    useEffect(() => {
        try {
            const service = CartServiceFactory.create();
            setCartService(service);
        }
        catch (err) {
            console.error("Failed to create CartService:", err);
        }
    }, []);
    const ensureCart = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!cartService)
            throw new Error("Cart service not ready");
        try {
            return yield cartService.getActiveCart();
        }
        catch (_a) {
            return yield cartService.createCart();
        }
    });
    const cartItems = useMemo(() => {
        if (!cart)
            return [];
        return cart.lineItems.map((item) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            return ({
                id: item.id,
                productId: item.productId,
                name: ((_a = item.name) === null || _a === void 0 ? void 0 : _a["en-US"]) || "",
                price: ((_b = item.price) === null || _b === void 0 ? void 0 : _b.value.centAmount) / 100,
                priceDiscounted: ((_f = (_e = (_d = (_c = item.discountedPricePerQuantity) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.discountedPrice) === null || _e === void 0 ? void 0 : _e.value) === null || _f === void 0 ? void 0 : _f.centAmount) / 100,
                quantity: item.quantity,
                image: ((_j = (_h = (_g = item.variant) === null || _g === void 0 ? void 0 : _g.images) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j.url) || "",
                key: ((_k = item.variant) === null || _k === void 0 ? void 0 : _k.key) || "",
            });
        });
    }, [cart]);
    const cartCount = useMemo(() => { var _a, _b; return (_b = (_a = cart === null || cart === void 0 ? void 0 : cart.lineItems) === null || _a === void 0 ? void 0 : _a.reduce((sum, item) => sum + item.quantity, 0)) !== null && _b !== void 0 ? _b : 0; }, [cart]);
    const totalItems = useMemo(() => cartItems.length, [cartItems]);
    const changeQuantity = (lineItemId, quantity) => __awaiter(void 0, void 0, void 0, function* () {
        if (!cartService || !cart)
            return;
        try {
            const updatedCart = yield cartService.changeLineItemQuantity(cart.id, cart.version, lineItemId, quantity);
            setCart(updatedCart);
        }
        catch (err) {
            console.error("Failed to change quantity:", err);
        }
    });
    const incrementQuantity = (lineItemId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!cart || !cartService)
            return;
        const lineItem = cart.lineItems.find((item) => item.id === lineItemId);
        if (!lineItem)
            return;
        yield changeQuantity(lineItemId, lineItem.quantity + 1);
    });
    const decrementQuantity = (lineItemId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!cart || !cartService)
            return;
        const lineItem = cart.lineItems.find((item) => item.id === lineItemId);
        if (!lineItem)
            return;
        const newQuantity = Math.max(1, lineItem.quantity - 1);
        yield changeQuantity(lineItemId, newQuantity);
    });
    const reloadCart = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!cartService)
            return;
        try {
            const updatedCart = yield cartService.getActiveCart();
            setCart(updatedCart);
        }
        catch (err) {
            console.warn("Failed to reload cart:", err);
        }
    });
    const clearEntireCart = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!cartService || !cart || cart.lineItems.length === 0)
            return;
        try {
            let updatedCart = yield cartService.getActiveCart();
            const lineItemIds = updatedCart.lineItems.map((item) => item.id);
            for (const lineItemId of lineItemIds) {
                const payload = {
                    version: updatedCart.version,
                    actions: [{ action: "removeLineItem", lineItemId }],
                };
                updatedCart = yield cartService.updateCart(updatedCart.id, payload);
            }
            setCart(updatedCart);
        }
        catch (err) {
            console.error("Failed to clear cart:", err);
        }
    });
    const removeLineItem = (lineItemId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!cartService || !cart)
            return;
        try {
            const freshCart = yield cartService.getActiveCart();
            const payload = {
                version: freshCart.version,
                actions: [{ action: "removeLineItem", lineItemId }],
            };
            const updatedCart = yield cartService.updateCart(freshCart.id, payload);
            setCart(updatedCart);
        }
        catch (err) {
            console.error("Failed to remove line item:", err);
        }
    });
    // const removeAllDiscountCodes = async () => {
    //   if (!cartService || !cart) return;
    //   try {
    //     let updatedCart = cart;
    //     for (const discount of cart.discountCodes) {
    //       const payload: MyCartUpdate = {
    //         version: updatedCart.version,
    //         actions: [
    //           {
    //             action: "removeDiscountCode",
    //             discountCode: {
    //               typeId: "discount-code",
    //               id: discount.discountCode.id,
    //             },
    //           },
    //         ],
    //       };
    //       updatedCart = await cartService.updateCart(updatedCart.id, payload);
    //     }
    //     setCart(updatedCart);
    //   } catch (err) {
    //     console.error("Failed to remove discount codes:", err);
    //   }
    // };
    const removeAllDiscountCodes = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!cartService || !cart)
            return;
        try {
            let updatedCart = yield cartService.getActiveCart();
            for (const discount of updatedCart.discountCodes) {
                const payload = {
                    version: updatedCart.version,
                    actions: [
                        {
                            action: "removeDiscountCode",
                            discountCode: {
                                typeId: "discount-code",
                                id: discount.discountCode.id,
                            },
                        },
                    ],
                };
                updatedCart = yield cartService.updateCart(updatedCart.id, payload);
            }
            setCart(updatedCart);
        }
        catch (err) {
            console.error("Failed to remove discount codes:", err);
        }
    });
    const clearCart = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!cartService)
            return;
        try {
            const newCart = yield cartService.createCart();
            setCart(newCart);
        }
        catch (err) {
            console.error("Failed to clear cart:", err);
        }
    });
    const addToCart = (productId_1, ...args_1) => __awaiter(void 0, [productId_1, ...args_1], void 0, function* (productId, variantId = 1) {
        if (!cartService)
            return;
        setLoadingItems((prev) => [...prev, productId]);
        try {
            const currentCart = cart !== null && cart !== void 0 ? cart : (yield ensureCart());
            const payload = {
                version: currentCart.version,
                actions: [
                    {
                        action: "addLineItem",
                        productId,
                        variantId,
                        quantity: 1,
                    },
                ],
            };
            const updatedCart = yield cartService.updateCart(currentCart.id, payload);
            setCart(updatedCart);
        }
        catch (err) {
            console.error("Add to cart failed:", err);
        }
        finally {
            setLoadingItems((prev) => prev.filter((id) => id !== productId));
        }
    });
    const removeFromCart = (productId, variantId) => {
        if (!cartService || !cart)
            return;
        const lineItem = cart.lineItems.find((item) => item.productId === productId &&
            (variantId ? item.variant.id === variantId : true));
        if (lineItem) {
            removeLineItem(lineItem.id);
        }
    };
    const isInCart = (productId, variantId) => {
        var _a, _b;
        return ((_b = (_a = cart === null || cart === void 0 ? void 0 : cart.lineItems) === null || _a === void 0 ? void 0 : _a.some((item) => item.productId === productId &&
            (variantId ? item.variant.id === variantId : true))) !== null && _b !== void 0 ? _b : false);
    };
    const isLoadingAddToCart = (productId) => {
        return loadingItems.includes(productId);
    };
    const applyPromoCode = (code) => __awaiter(void 0, void 0, void 0, function* () {
        if (!cartService || !cart)
            return;
        try {
            const updatedCart = yield cartService.addDiscountCode(cart.id, cart.version, code.toUpperCase());
            setCart(updatedCart);
        }
        catch (err) {
            console.error("Failed to apply promo code:", err);
            throw err;
        }
    });
    const removePromoCode = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!cartService || !cart)
            return;
        try {
            const cart = yield cartService.getActiveCart();
            const codeID = cart.discountCodes[0].discountCode.id;
            if (codeID) {
                const updatedCart = yield cartService.removeDiscountCode(cart.id, cart.version, codeID);
                setCart(updatedCart);
            }
        }
        catch (err) {
            console.error("Failed to remove promo code:", err);
            throw err;
        }
    });
    const resetCartService = () => {
        const newService = CartServiceFactory.create();
        setCartService(newService);
    };
    useEffect(() => {
        if (!cartService)
            return;
        const initCart = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const initialCart = yield ensureCart();
                setCart(initialCart);
            }
            catch (err) {
                console.error("Cart init failed:", err);
            }
        });
        initCart();
    }, [cartService]);
    if (!cartService)
        return _jsx("div", { children: "Loading cart..." });
    return (_jsx(CartContext.Provider, { value: {
            cart,
            cartCount,
            cartItems,
            addToCart,
            isInCart,
            isLoadingAddToCart,
            clearCart,
            reloadCart,
            removeLineItem,
            clearEntireCart,
            removeFromCart,
            totalItems,
            changeQuantity,
            incrementQuantity,
            decrementQuantity,
            applyPromoCode,
            removePromoCode,
            cartService,
            removeAllDiscountCodes,
            resetCartService,
        }, children: children }));
};
export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error("useCart must be used within a CartProvider");
    }
    return context;
};
